import { MODAL_AUTH } from '@components/App/Modal/constants'
import { Badge, type BadgeType } from '@octopus-do/octopus-storybook'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import type { GtmTriggerName } from '@/constants/gtm'
import { useAuth } from '@/hooks'
import { clsJoin } from '@/utils'
import css from './Tabs.module.scss'

type TabItem<TProps> = {
  tab: {
    title: string
    Icon?:
      | React.FC<React.SVGProps<SVGSVGElement>>
      | React.MemoExoticComponent<React.FC<React.SVGProps<SVGSVGElement>>>
    badge?: BadgeType
  }
  Component: React.ComponentType<TProps>
  componentProps?: TProps
}

interface Props<TTabs extends TabItem<any>[]> {
  tabs: TTabs
  size?: 'medium' | 'large'
  selectedIndex?: number
  upgrade?: {
    show: boolean
    text: string
    plan: 'pro' | 'team'
    gtmProBadgeEvent: GtmTriggerName
  }
  onSelect?: (index: number) => void
}

export const Tabs = <TTabs extends TabItem<any>[]>({
  tabs,
  size,
  selectedIndex,
  upgrade,
  onSelect,
}: Props<TTabs>) => {
  const dispatch = useDispatch()

  const [active, setActive] = useState(selectedIndex || 0)

  const { authenticated } = useAuth()

  useEffect(() => {
    if (selectedIndex !== undefined) {
      setActive(selectedIndex)
    }
  }, [selectedIndex])

  const handleItemClick = (index: number) => {
    setActive(index)
    onSelect?.(index)
  }

  const onClickUpgrade = async () => {
    if (!authenticated) {
      dispatch(openModal(MODAL_AUTH))
    }
  }

  const ActiveTab = tabs[active]

  const tabItemCn = (index: number) =>
    clsJoin(css.item, css[size || 'medium'], active === index && css.active)

  return (
    <div>
      <ul className={clsJoin(css.list, css[size || 'medium'])}>
        {tabs.map(({ tab: { title, Icon, badge } }, index) => (
          <li
            key={`${title}/${index}`}
            className={tabItemCn(index)}
            onClick={() => handleItemClick(index)}
          >
            {Icon && <Icon />}
            {title}
            {badge && <Badge type={badge} />}
          </li>
        ))}
      </ul>

      {upgrade && upgrade.show && (
        <div className={css.upgrade} onClick={onClickUpgrade}>
          <Badge type={upgrade.plan} /> {upgrade.text}
        </div>
      )}

      <div className={clsJoin(css.content, css[size || 'medium'])}>
        <ActiveTab.Component {...ActiveTab.componentProps} />
      </div>
    </div>
  )
}
